import React from "react";
import { useTranslation } from "react-i18next";

import {
  useAssignEveryoneToManager,
  useAssignManagerToResource,
  useRemoveSubordinates,
  useRetrieveResourceSubordinates,
  useUnAssignManagerFromResource,
} from "@planzone/view-models";
import { Flag, InfiniteScrollRoot } from "@common/infiniteScroll";
import { SelectList } from "@common/selectList";
import { AddResourceIcon } from "@scenes/project/resources/menu/addResourceIcon";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { Resource as ResourceComponent } from "./resource";
import { Props } from "./types";
import styles from "./styles.scss";
import { ResourceTypes } from "@planzone/dto";

function AllocationManagement(props: Props) {
  const { t } = useTranslation();
  const {
    resources,
    retrieveResourceSubordinates,
    isRequestSuccess,
    isRequestPending,
    canLoadMore,
    currentPage,
  } = useRetrieveResourceSubordinates(props.resource);
  const { assignManagerToResource } = useAssignManagerToResource(
    props.resource,
  );
  const { unAssignManagerFromResource } = useUnAssignManagerFromResource(
    props.resource,
  );
  const { assignEveryoneToManager, isRequestPending: isEveryonePending } =
    useAssignEveryoneToManager(props.resource);
  const { removeSubordinates, isRequestPending: isRemovalPending } =
    useRemoveSubordinates(props.resource);

  React.useEffect(() => {
    !isRequestSuccess &&
      !isRequestPending &&
      retrieveResourceSubordinates({
        resource: props.resource,
        page: 0,
        size: 5,
      });
  }, [props.resource.uuid]);

  return (
    <InfiniteScrollRoot canFetchOnIdle>
      <div className={styles.allocationManagement}>
        <h2 className={styles.title}>{t("editResource.allocationTitle")} :</h2>
        <div className={styles.resources}>
          {resources.map((resource, key) => (
            <div className={styles.resource} key={key}>
              <ResourceComponent
                resource={resource}
                remove={(resource) => {
                  unAssignManagerFromResource({
                    resource,
                    manager: props.resource,
                  });
                }}
              />
            </div>
          ))}
          <Flag
            name="subordinates"
            isDisabled={isRequestPending || !canLoadMore}
            retrieveItems={() =>
              retrieveResourceSubordinates({
                resource: props.resource,
                page: currentPage,
                size: 5,
              })
            }
          />
        </div>
        <SelectList
          rootClassName={styles.addAllocation}
          listClassName={styles.list}
        >
          {() => ({
            input: (
              <div className={styles.iconBox}>
                <AddResourceIcon className={styles.icon} />
              </div>
            ),
            list: (
              <div>
                <ResourceAllocationMenu
                  allocatedResources={resources}
                  openedViews={["resources"]}
                  select={(r) =>
                    r.map((value) =>
                      assignManagerToResource({
                        resource: value,
                        manager: props.resource,
                      }),
                    )
                  }
                  unSelect={(r) =>
                    r.map((value) => {
                      unAssignManagerFromResource({
                        resource: value,
                        manager: props.resource,
                      });
                    })
                  }
                  everyone={{
                    select: () =>
                      assignEveryoneToManager({ manager: props.resource }),
                    isPending: isEveryonePending,
                    excludedResource: props.resource,
                  }}
                />
                {resources.length > 0 && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() =>
                      !isRemovalPending &&
                      removeSubordinates({ manager: props.resource })
                    }
                  >
                    {t("planzoneMembersPage.resources.clearSubordinates")}
                  </button>
                )}
              </div>
            ),
          })}
        </SelectList>
      </div>
    </InfiniteScrollRoot>
  );
}

export { AllocationManagement };
